import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

const PaginationContext = createContext();

const getPageSizeForRoute = (pathname) => {
  if (pathname.includes('/Pacientes') || pathname.includes('/Terapeutas')) {
    return 12;
  }
  return 10;
};

// Normaliza o pathname para a rota base (ex: /Pacientes/PerfilPaciente/123 -> /Pacientes)
const getBaseRoute = (pathname) => {
  // Lista de rotas base que devem manter paginação entre sub-rotas
  const baseRoutes = [
    '/Pacientes',
    '/Terapeutas',
    '/Avisos',
    '/Triagens',
    '/Responsaveis',
    '/Usuarios',
    '/Salas',
    '/Planos',
    '/PrestServicos'
  ];

  for (const baseRoute of baseRoutes) {
    if (pathname.startsWith(baseRoute)) {
      // Se for exatamente a rota base ou uma sub-rota, retorna a base
      if (pathname === baseRoute || pathname.startsWith(baseRoute + '/')) {
        return baseRoute;
      }
    }
  }

  // Se não encontrar, retorna o pathname original
  return pathname;
};

export const PaginationProvider = ({ children, pathname }) => {
  const [page, setPageState] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Calculate pageSize based on pathname using useMemo
  const pageSize = useMemo(() => getPageSizeForRoute(pathname), [pathname]);

  // Get base route for storage key
  const baseRoute = useMemo(() => getBaseRoute(pathname), [pathname]);

  const STORAGE_PREFIX = 'pagination_state';
  const buildStorageKey = (p) => `${STORAGE_PREFIX}_${p || ''}`;

  // Load stored page on base route change (or first mount)
  useEffect(() => {
    const key = buildStorageKey(baseRoute);
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (typeof parsed.page === 'number' && parsed.page >= 0) {
          setPageState(parsed.page);
        } else {
          setPageState(0);
        }
      } else {
        setPageState(0);
      }
    } catch {
      setPageState(0);
    }
    // clear totals on route change; will be repopulated by the listing page
    setTotalElements(0);
    setTotalPages(0);
  }, [baseRoute]);

  // Persist current page when it changes
  useEffect(() => {
    const key = buildStorageKey(baseRoute);
    try {
      sessionStorage.setItem(key, JSON.stringify({ page }));
    } catch {
      // ignore
    }
  }, [page, baseRoute]);

  const setPage = useCallback((updater) => {
    setPageState((prev) => {
      const nextValue = typeof updater === 'function' ? updater(prev) : updater;
      if (Number.isNaN(nextValue) || nextValue < 0) return 0;
      return nextValue;
    });
  }, []);

  const resetPagination = useCallback(() => {
    setPageState(0);
    setTotalElements(0);
    setTotalPages(0);
  }, []);

  const value = useMemo(() => ({
    page,
    setPage,
    pageSize,
    totalElements,
    setTotalElements,
    totalPages,
    setTotalPages,
    resetPagination
  }), [page, pageSize, totalElements, totalPages, setPage, resetPagination]);

  return (
    <PaginationContext.Provider value={value}>
      {children}
    </PaginationContext.Provider>
  );
};

export const usePagination = () => useContext(PaginationContext);
